import { IconCalendar } from "@dream/ui";
import { Link } from "@tanstack/react-router";
import { CopyIcon, Volume2Icon } from "lucide-react";
import {
  Attachment,
  AttachmentInfo,
  AttachmentPreview,
  Attachments,
} from "@/components/ai-elements/attachments";
import {
  Message,
  MessageAction,
  MessageActions,
  MessageContent,
  MessageResponse,
} from "@/components/ai-elements/message";
import { Tool, ToolContent, ToolHeader, ToolOutput } from "@/components/ai-elements/tool";
import { calendarSearch } from "../router.tsx";
import type { ChatLine } from "./moon-types.ts";

export function MoonLine({
  line,
  onSpeak,
  onOpenCalendar,
}: {
  line: ChatLine;
  onSpeak: (text: string) => void;
  onOpenCalendar: () => void;
}) {
  const created = (line.tools ?? []).filter((tool) => tool.ok && tool.name.startsWith("create_"));
  const failed = (line.tools ?? []).filter((tool) => !tool.ok);

  return (
    <Message from={line.role}>
      <MessageContent>
        {line.presentation !== undefined ? (
          <a
            className="text-sm font-medium text-primary underline-offset-2 hover:underline"
            href={line.presentation}
            rel="noreferrer"
            target="_blank"
          >
            Apresentação Figma
          </a>
        ) : null}
        {line.pdfName !== undefined ? (
          <Attachments variant="inline">
            <Attachment
              data={{
                id: line.pdfName,
                type: "file",
                filename: line.pdfName,
                mediaType: "application/pdf",
                url: "",
              }}
            >
              <AttachmentPreview />
              <AttachmentInfo />
            </Attachment>
          </Attachments>
        ) : null}
        {line.audioUrl !== undefined ? (
          <audio className="h-9 w-full max-w-xs" controls preload="metadata" src={line.audioUrl}>
            <track kind="captions" />
          </audio>
        ) : null}
        {line.transcript !== undefined && line.transcript.trim() !== "" ? (
          <p className="text-sm text-muted-foreground">{line.transcript}</p>
        ) : null}
        {line.role === "assistant" ? (
          <MessageResponse>{line.content}</MessageResponse>
        ) : (
          <p className="whitespace-pre-wrap leading-5">{line.content}</p>
        )}
        {(line.tools ?? []).map((tool) => (
          <Tool defaultOpen={false} key={tool.id}>
            <ToolHeader
              state={tool.ok ? "output-available" : "output-error"}
              title={tool.summary}
              type={`tool-${tool.name}`}
            />
            <ToolContent>
              <ToolOutput
                errorText={tool.ok ? undefined : tool.summary}
                output={tool.ok ? tool.summary : undefined}
              />
            </ToolContent>
          </Tool>
        ))}
        {created.length > 0 ? (
          <p className="text-sm text-muted-foreground">
            {created.length} entrega(s) no calendário.{" "}
            <Link
              className="font-medium text-primary hover:underline"
              search={calendarSearch()}
              to="/calendario"
            >
              Conferir datas
            </Link>
          </p>
        ) : null}
        {failed.length > 0 ? (
          <p className="text-sm text-destructive">
            {failed.map((tool) => tool.summary).join(" · ")}
          </p>
        ) : null}
      </MessageContent>
      {line.role === "assistant" ? (
        <MessageActions>
          <MessageAction
            label="Copiar"
            onClick={() => void navigator.clipboard.writeText(line.content)}
            tooltip="Copiar"
          >
            <CopyIcon className="size-4" />
          </MessageAction>
          <MessageAction label="Falar" onClick={() => onSpeak(line.content)} tooltip="Falar">
            <Volume2Icon className="size-4" />
          </MessageAction>
          <MessageAction label="Calendário" onClick={onOpenCalendar} tooltip="Abrir o calendário">
            <IconCalendar className="size-4" />
          </MessageAction>
        </MessageActions>
      ) : null}
    </Message>
  );
}
